import { useTranslation } from "react-i18next";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { cn } from "@core/utils";
import type { AppLanguage } from "@core/libs/i18n";

/**
 * Language endonyms shown on the segments — proper names, identical across locales, so they
 * are NOT translated. French is listed first (it is the default). Selecting one calls i18next
 * `changeLanguage`; the `languageChanged` listener wired in main.tsx updates `<html lang/dir>`.
 * `aria-label`s ARE translated (common namespace).
 *
 * Look (Figma "Language Switch"): a full pill on a soft track, 36x28 segments, the selected one
 * raised with a hairline border. The border is transparent on the other segment so selecting
 * never shifts the layout.
 */
const LANGUAGES: { code: AppLanguage; short: string }[] = [
  { code: "fr", short: "FR" },
  { code: "en", short: "EN" },
];

export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation("common");
  const current = i18n.resolvedLanguage ?? "fr";

  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[current]}
      onSelectionChange={(keys) => {
        const next = [...keys][0];
        if (typeof next === "string" && next !== current) {
          void i18n.changeLanguage(next);
        }
      }}
      aria-label={t("language.switch")}
      className={cn(
        "inline-flex items-center gap-xxs rounded-full bg-ink-100 p-xxs",
        className
      )}
    >
      {LANGUAGES.map(({ code, short }) => (
        <ToggleButton
          key={code}
          id={code}
          aria-label={t(`language.${code}`)}
          className={cn(
            "flex h-7 w-9 cursor-pointer items-center justify-center rounded-full border border-transparent outline-none transition-colors",
            "text-xs-medium text-(--text-secondary)",
            "focus-visible:ring-2 focus-visible:ring-focus-ring",
            "selected:border-(--border-default) selected:bg-ink-25 selected:font-semibold selected:text-(--text-primary)"
          )}
        >
          {short}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
